import {
	ChevronDownIcon,
	HotIcon,
	RateIcon,
	RecommendIcon,
	SearchIcon,
	Time2Icon,
	TimeIcon,
} from "@/components/icons";
import NoData from "@/components/ui/no-data";
import { Skeleton } from "@/components/ui/skeleton";
import { AuthProtected } from "@/lib/auth/hoc";
import { BaseResponse, request } from "@/lib/request";
import { cn, formatPopularity, fromNow } from "@/lib/utils";
import { localizeHref } from "@/paraglide/runtime";
import { createFileRoute } from "@tanstack/react-router";
import { debounce } from "lodash-es";
import { useEffect, useRef, useState } from "react";

import { TagItem, TagList } from "@/components/business/Tag";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Rating } from "@/components/ui/rating";
import { Switch } from "@/components/ui/switch";
import { m } from "@/paraglide/messages";
import { Creation, CreationList, QueryCreationOrderField, Tag } from "@/types";
import {
	type QueryClient,
	useInfiniteQuery,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { TagResponse, listTag } from "./edit/-comps/api";

const sortItems = [
	{
		label: m.recommend_sort(),
		icon: <RecommendIcon className="w-3 h-3 dark:[&>g>path]:stroke-t2" />,
		value: QueryCreationOrderField.RECOMMEND,
	},
	{
		label: m.hot_sort(),
		icon: <HotIcon className="w-3 h-3 dark:[&>path]:stroke-t2" />,
		value: QueryCreationOrderField.POPULARITY,
	},
	{
		label: m.score_sort(),
		icon: <RateIcon className="w-3 h-3 dark:[&>path]:stroke-t2" />,
		value: QueryCreationOrderField.SCORE,
	},
	{
		label: m.new_sort(),
		icon: <TimeIcon className="w-3 h-3 dark:[&>path]:stroke-t2" />,
		value: QueryCreationOrderField.UPDATE_TIME,
	},
];

const getList = async (
	page: number,
	order_filed: QueryCreationOrderField,
	searchValue: string,
	tags: Tag[],
) => {
	return await request.get("/aifk/creation/query", {
		page_num: page,
		page_size: 20,
		order_filed,
		title: searchValue,
		tags: tags.map((tag) => tag.tag_id).join(","),
	});
};

export const Route = createFileRoute("/$lang/")({
	component: RouteComponent,
	validateSearch: (search) => {
		return {
			search: search.search,
		};
	},
});

function RouteComponent() {
	const queryClient = useQueryClient();
	const [sort, setSort] = useState(sortItems[0]);
	const [searchValue, setSearchValue] = useState("");
	const [tags, setTags] = useState<Tag[]>([]);

	const { data: tagRes } = useQuery<BaseResponse<TagResponse>>({
		queryKey: ["listTag"],
		queryFn: listTag,
	});

	const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
		useInfiniteQuery<BaseResponse<CreationList>, Error>({
			queryKey: [
				"aifk/creation/query",
				sort.value,
				searchValue,
				tags.map((tag) => tag.tag_id).join(","),
			],
			queryFn: ({ pageParam = 1 }) =>
				getList(pageParam as number, sort.value, searchValue, tags),
			getNextPageParam: (lastPage, pages) => {
				return lastPage.data?.total > pages.length * 20
					? pages.length + 1
					: undefined;
			},
			initialPageParam: 1,
		});

	const list = data?.pages.flatMap((page) => page.data?.list || []) || [];

	useEffect(() => {
		const handleScroll = debounce(() => {
			const scrollTop = window.scrollY;
			const clientHeight = document.documentElement.clientHeight;
			const scrollHeight = document.documentElement.scrollHeight;

			// 增加更严格的滚动触发条件
			if (
				scrollTop + clientHeight >= scrollHeight - 100 &&
				hasNextPage &&
				!isFetchingNextPage
			) {
				fetchNextPage();
			}
		}, 200); // 200ms 的防抖时间

		const content = document.getElementById("content");
		if (content) {
			content.addEventListener("scroll", handleScroll);
		}
		return () => {
			handleScroll.cancel(); // 清理防抖
			if (content) {
				content.removeEventListener("scroll", handleScroll);
			}
		};
	}, [hasNextPage, fetchNextPage, isFetchingNextPage]);

	return (
		<div className="max-md:px-4">
			<div className="flex flex-row items-center justify-center">
				<Search
					allTags={tagRes?.data?.list ?? []}
					searchValue={searchValue}
					setSearchValue={(val) => {
						setSearchValue(val);
					}}
					tags={tags}
					setTags={(newTags) => {
						setTags(newTags);
					}}
					queryClient={queryClient}
				/>
			</div>
			<Sort
				queryClient={queryClient}
				sort={sort}
				setSort={(item) => {
					setSort(item);
				}}
			/>
			<div className="grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 grid-cols-2 md:gap-5 gap-2">
				{isLoading
					? // 显示骨架屏
						Array.from({ length: 10 }).map((_, index) => (
							<StoryCardSkeleton key={`skeleton-${index}`} />
						))
					: // 显示实际内容
						list.map((item, _index) => (
							<StoryCard
								key={`story-${item.creation_id}-${_index}`}
								item={item}
							/>
						))}
			</div>
			{(isLoading || isFetchingNextPage) && (
				<div className="text-center text-t3 text-sm py-4">
					{m.common_loading()}
				</div>
			)}
			{/* {!hasNextPage && list.length > 0 && !isLoading && (
				<div className="text-center text-t3 text-sm py-4 ">没有更多了</div>
			)} */}
			{list.length === 0 && !isLoading && <NoData />}
		</div>
	);
}

function Sort({
	queryClient,
	sort,
	setSort,
}: {
	queryClient: QueryClient;
	sort: (typeof sortItems)[number];
	setSort: (sort: (typeof sortItems)[number]) => void;
}) {
	return (
		<div className="relative mt-18">
			<div className="mt-5 mb-2.5 flex items-center justify-between">
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<div
							className={cn(
								"h-8.5 cursor-pointer rounded-full bg-card px-4 inline-flex gap-3 justify-between items-center text-t2",
								"transition-transform duration-200 data-[state=open]:[&>svg]:rotate-180",
							)}
						>
							<div className="flex flex-row gap-2 items-cente text-sm">
								<div className="w-4 h-4 [&>svg]:w-4 [&>svg]:h-4 relative top-0.5">
									{sort.icon}
								</div>

								<span>{sort.label}</span>
							</div>

							<ChevronDownIcon className="w-3 h-3 dark:[&>path]:stroke-t2 transition-transform duration-200" />
						</div>
					</DropdownMenuTrigger>
					<DropdownMenuContent className="min-w-35 p-[5px] border-none shadow-card cursor-pointer">
						{sortItems.map((item) => (
							<DropdownMenuItem
								key={item.label}
								className="h-8 pl-4 cursor-pointer py-2 hover:!bg-c1 text-t2 rounded-[6px]"
								onClick={() => {
									setSort(item);
								}}
							>
								<div className="flex flex-row gap-2 items-center">
									{item.icon}
									<span>{item.label}</span>
								</div>
							</DropdownMenuItem>
						))}
					</DropdownMenuContent>
				</DropdownMenu>
				<NSFWSwitch />
			</div>
		</div>
	);
}

function NSFWSwitch() {
	return (
		<div className="flex items-center gap-1 cursor-pointer select-none">
			<Switch
				id="nsfw_switch"
				className="ml-auto"
				defaultChecked={localStorage.getItem("NSFW_SWITCH") === "true"}
				onCheckedChange={(checked) => {
					localStorage.setItem("NSFW_SWITCH", checked.toString());
				}}
			/>
			<label className="text-t2" htmlFor="nsfw_switch">
				NSFW
			</label>
		</div>
	);
}

function Search({
	searchValue,
	setSearchValue,
	allTags,
	tags,
	setTags,
	queryClient,
}: {
	searchValue: string;
	setSearchValue: (value: string) => void;
	allTags: Tag[];
	tags: Tag[];
	setTags: (tags: Tag[]) => void;
	queryClient: QueryClient;
}) {
	const popoverRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [isExpanded, setIsExpanded] = useState(false);

	useEffect(() => {
		if (!isExpanded) return;
		function handleClickOutside(event: MouseEvent) {
			if (
				popoverRef.current?.contains(event.target as Node) ||
				buttonRef.current?.contains(event.target as Node)
			) {
				return;
			}
			setIsExpanded(false);
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isExpanded]);

	return (
		<div className="md:w-2/3 max-w-[900px] w-full relative">
			<div className="flex h-10 rounded-full flex-row px-4 items-center gap-1 bg-bg2">
				<SearchIcon className="min-w-5.5 min-h-5.5" />
				<input
					type="text"
					value={searchValue}
					onChange={(e) => {
						setSearchValue(e.target.value);
					}}
					placeholder={m.home_search_placeholder()}
					className="bg-transparent text-t2 flex-1 outline-none"
				/>
			</div>
			<div
				ref={popoverRef}
				className={cn(
					"text-sm text-t3 mt-2 flex gap-2 items-start absolute p-4 z-10 transition-all duration-200",
					isExpanded
						? "max-h-[200px] overflow-y-auto bg-card rounded-[20px] shadow-card"
						: "max-h-11 overflow-hidden",
				)}
			>
				<div
					className={cn("flex flex-wrap gap-2 items-center overflow-hidden")}
				>
					<span className="text-t3">
						<m.home_recommend_tag />
					</span>
					{allTags?.map((item, _index) => (
						<TagItem
							type="search"
							active={tags.some((tag) => tag.tag_id === item.tag_id)}
							key={`r-${item.tag_id}-${_index}`}
							label={item.name}
							onClick={() => {
								// 单选模式：如果已选中则取消，否则替换为当前标签
								if (tags.some((tag) => tag.tag_id === item.tag_id)) {
									setTags([]);
								} else {
									setTags([item]);
								}
							}}
						/>
					))}
				</div>
				<button
					ref={buttonRef}
					type="button"
					onClick={() => setIsExpanded((v) => !v)}
					className={cn(
						"flex items-center pt-2 gap-1 text-t2 hover:text-t1 transition-colors",
					)}
				>
					<ChevronDownIcon
						className={`w-4 h-4 cursor-pointer transition-transform duration-200 ${
							isExpanded ? "rotate-180" : ""
						}`}
					/>
				</button>
			</div>
		</div>
	);
}

function StoryCardSkeleton() {
	return (
		<div className="rounded-[20px] shadow-card bg-card overflow-hidden">
			<div className="relative rounded-[20px] overflow-hidden">
				<Skeleton className="w-full aspect-[307/255] rounded-[20px]" />
				<div className="flex flex-row justify-between items-center">
					<div className="absolute top-2.5 left-4 flex items-center gap-1.5 z-2">
						<Skeleton className="w-4.5 h-4.5 rounded-full" />
						<Skeleton className="w-16 h-3 rounded" />
					</div>
					<div className="absolute top-2.5 right-4 flex items-center gap-1.5 z-2">
						<Skeleton className="w-3 h-3 rounded" />
						<Skeleton className="w-20 h-3 rounded" />
					</div>
				</div>
			</div>
			<div className="p-4">
				<Skeleton className="w-3/4 h-6 rounded mb-3.5" />
				<div className="flex flex-row gap-2 items-center flex-wrap mb-2.5">
					<Skeleton className="w-16 h-6 rounded-full" />
					<Skeleton className="w-20 h-6 rounded-full" />
					<Skeleton className="w-14 h-6 rounded-full" />
				</div>
				<Skeleton className="w-full h-4 rounded mb-2" />
				<Skeleton className="w-2/3 h-4 rounded mb-2.5" />
				<div className="flex flex-row gap-2 items-center justify-between">
					<div className="flex flex-row gap-1.5 items-center">
						<Skeleton className="w-3 h-3 rounded" />
						<Skeleton className="w-8 h-3 rounded" />
					</div>
					<div className="flex flex-row gap-1 items-center">
						<Skeleton className="w-16 h-3 rounded" />
					</div>
				</div>
			</div>
		</div>
	);
}

function StoryCard({ item }: { item: Creation }) {
	const navigate = useNavigate();
	const cardRef = useRef<HTMLDivElement>(null);
	const [fixedHeight, setFixedHeight] = useState<number | undefined>(undefined);
	const [isHover, setIsHover] = useState(false);

	// 进入 hover 时记录高度
	const handleMouseEnter = () => {
		if (cardRef.current) {
			setFixedHeight(cardRef.current.offsetHeight);
		}
		setIsHover(true);
	};

	// 离开 hover 时恢复自适应
	const handleMouseLeave = () => {
		setIsHover(false);
		setFixedHeight(undefined);
	};

	return (
		<AuthProtected>
			<div
				ref={cardRef}
				style={fixedHeight ? { height: fixedHeight } : undefined}
				className="rounded-[20px] relative cursor-pointer group shadow-card transition-all duration-300 bg-card overflow-hidden flex flex-col justify-between"
				onClick={() => {
					navigate({
						to: localizeHref("/story/$creation_id/$dialogue_id"), // todo  -- 跳转链接待接入
						params: { creation_id: item.creation_id, dialogue_id: "-" },
					});
				}}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				<div
					className={cn(
						"relative rounded-[20px] overflow-hidden transition-all duration-300",
						isHover && "h-full",
					)}
				>
					<img
						src={item?.cover_image}
						className={cn(
							"w-full max-w-full h-auto aspect-[307/255] object-cover transition-all duration-300",
							isHover
								? "scale-101 blur-[0.5px] brightness-90 absolute top-0 left-0 h-full w-full"
								: "scale-100 blur-0 brightness-100",
						)}
						alt="cover"
					/>
					<div
						className={cn(
							"flex flex-row justify-between items-center transition-all duration-300 opacity-100",
							isHover && "opacity-0",
						)}
					>
						<div className="absolute top-2.5 left-4 flex items-center gap-1.5 z-2">
							<img
								className="w-4.5 h-4.5 rounded-full object-cover"
								src={item?.author?.avatar}
								alt="cover"
							/>
							<span className="text-t4 text-xs max-w-5 md:max-w-20 truncate">
								{item?.author?.name}
							</span>
						</div>
						<div className="absolute top-2.5 right-4 flex items-center gap-1.5 z-2">
							<Time2Icon className="w-3 h-3" />
							<span className="text-t4 text-xs">
								{fromNow(item?.updated_time / 1000000000)}
							</span>
						</div>
					</div>
				</div>
				<div
					className={cn(
						"absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none transition-all duration-300",
						isHover ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2",
					)}
				/>
				<div
					className={cn(
						"p-4 transition-all duration-300 flex flex-col flex-1",
						isHover &&
							"justify-end absolute bottom-0 left-0 h-full w-full bg-gradient-to-t from-black to-black/10 ",
					)}
				>
					<div className={cn("flex-1", isHover && "flex-none")}>
						<h6
							className={cn(
								"text-t1 text-lg leading-6 mb-3.5 line-clamp-1 break-words",
								isHover && "line-clamp-2 text-white",
							)}
						>
							{item?.title}
						</h6>
						<div className={cn("h-6", isHover && "h-14")}>
							<TagList
								tags={item?.tags?.map((tag) => tag.name) || []}
								isHover={isHover}
							/>
						</div>
						<p
							className={cn(
								"text-t2 text-sm leading-6 mt-2.5 line-clamp-2 break-words",
								isHover && "line-clamp-6 text-[#C8C8C8]",
							)}
						>
							{item?.brief_introduction}
						</p>
					</div>
					<div
						className={cn(
							"flex flex-row gap-2 items-center mt-2.5 justify-between transition-all duration-300	",
							isHover && "hidden",
						)}
					>
						<div className="flex flex-row gap-1.5 items-center">
							<img
								src="/assets/icons/fire.svg"
								className="w-3 h-3"
								alt="fire"
							/>
							<span className="text-t3 text-xs">
								{formatPopularity(item?.popularity ?? 0)}
							</span>
						</div>
						<div className="flex flex-row gap-1.5 items-center">
							<Rating initialRating={item?.score ?? 0} readonly />
						</div>
					</div>
				</div>
			</div>
		</AuthProtected>
	);
}
